import { requestField, weekdaysOf } from "@/features/profile/changeRequest";
import type { EmployeeProfile, ProfileFileKind } from "@/features/profile/types";

/**
 * Corrections HR asked of an approved employee.
 *
 * HR crosses the fields that are wrong and sends the profile back; the
 * account stays active, so the employee keeps the rota, leave, documents and
 * pay while they put the crossed fields right. Those fields go straight on
 * the record, not through a change request, because HR asked for them; the
 * profile goes back to HR when the employee says they have finished.
 */

/** The upload slot for each copy of a paper on the record. */
export const FILE_KIND_BY_FIELD: Readonly<Record<string, ProfileFileKind>> = {
  profile_picture_file_id: "profile_picture",
  emirates_id_front_file_id: "emirates_id_front",
  emirates_id_back_file_id: "emirates_id_back",
  passport_file_id: "passport_copy",
  visa_file_id: "visa_copy",
  cv_file_id: "cv",
  degree_file_id: "degree_certificate",
  experience_letter_file_id: "experience_letter",
  other_file_id: "other",
  iban_letter_file_id: "iban_letter",
  labour_card_file_id: "labour_card",
  health_insurance_file_id: "health_insurance",
  police_clearance_file_id: "police_clearance",
  family_book_file_id: "family_book",
  signature_file_id: "signature",
};

export function isFileField(field: string): boolean {
  return field.endsWith("_file_id");
}

/** Where the corrections stand, from the employee's side. */
export type CorrectionsState = "none" | "with_me" | "with_hr";

export function correctionsState(
  profile: Pick<EmployeeProfile, "corrections_requested_at" | "corrections_returned_at"> | undefined,
): CorrectionsState {
  if (!profile?.corrections_requested_at) return "none";
  return profile.corrections_returned_at ? "with_hr" : "with_me";
}

/**
 * The typed fields to correct, in the order given, the days off as one
 * question. Files are uploaded on their own and are not in the form.
 */
export function correctionFormFields(fields: readonly string[]): string[] {
  return fields.filter((field) => !isFileField(field) && field !== "days_off_rotate");
}

/** The form's starting values: what the record holds now, as strings. */
export function correctionForm(
  fields: readonly string[],
  profile: EmployeeProfile,
): Record<string, string> {
  const current = profile as unknown as Record<string, unknown>;
  const form: Record<string, string> = {};
  for (const field of correctionFormFields(fields)) {
    if (field === "weekly_off_days") {
      form.days_off_rotate = profile.days_off_rotate ? "true" : "";
      form.weekly_off_days = (profile.weekly_off_days ?? []).join(",");
      continue;
    }
    form[field] = String(current[field] ?? "");
  }
  return form;
}

/**
 * What differs from the record, ready to send. A field cleared in the form
 * goes as null; "8" and "8.00" hours are the same hours.
 */
export function correctionChanges(
  fields: readonly string[],
  profile: EmployeeProfile,
  form: Record<string, string>,
): Record<string, unknown> {
  const current = profile as unknown as Record<string, unknown>;
  const changes: Record<string, unknown> = {};
  for (const field of correctionFormFields(fields)) {
    if (field === "weekly_off_days") {
      const rotates = form.days_off_rotate === "true";
      const days = rotates ? [] : weekdaysOf(form.weekly_off_days);
      if (rotates !== Boolean(profile.days_off_rotate)) changes.days_off_rotate = rotates;
      if (days.join(",") !== (profile.weekly_off_days ?? []).join(",")) {
        changes.weekly_off_days = days;
      }
      continue;
    }
    const typed = (form[field] ?? "").trim();
    const stored = String(current[field] ?? "");
    const sameFigure =
      typed !== "" && requestField(field).kind === "number" && Number(typed) === Number(stored);
    if (typed !== stored && !sameFigure) changes[field] = typed === "" ? null : typed;
  }
  return changes;
}
